/* eslint-disable react/jsx-key */

import React from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import CustomerLogAPI from 'api/faber/CustomerLogAPI'
import { AxiosError } from 'axios'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import { Field, FieldArray, Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date , setDate, setFieldVal} = props

  const handleChangeDate = (newValue: Date | null) => {
    setDate(newValue)
    setFieldVal('departureDate', newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label={label}
        inputFormat='dd-MM-yyyy'
        views={['year', 'month', 'day']}
        value={date}
        onChange={handleChangeDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
  setFieldVal: (field: string, value: Date | null) => void
}

const formikInitialValues = {
  flightCode: '',
  departureAirportCode: '',
  arrivalAirportCode: '',
  departureDate: new Date(),
  title: 'Mr',
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  additionalPassengerDetails: [],
  // Give culture code by default
  cultureCode: 'en-GB'
}

const NewFlightRegistrationSchema = Yup.object().shape({
  flightCode: Yup.string().trim().required("Flight code is required"),
  departureAirportCode: Yup.string()
    .required("Departure airport code is required")
    .trim()
    .matches(/^[A-Z]{3}$/, "Airport codes are 3 letters long and should be characters between A-Z"),
  arrivalAirportCode: Yup.string()
    .required("Arrival airport code is required")
    .trim()
    .matches(/^[A-Z]{3}$/, "Airport codes are 3 letters long and should be characters between A-Z"),
  departureDate: Yup.date().required("Date is required"),
  title: Yup.string().required("Title is required"),
  firstName: Yup.string()
    .trim()
    .required("First name is required"),
  lastName: Yup.string()
    .trim()
    .required("Last name is required"),
  email: Yup.string()
    .trim()
    .email("Please enter a valid email address")
    .required("E-mail is required"),
  mobile: Yup.string()
    .trim()  
    .required("Mobile number is required")
    .matches(/^\+[0-9]+$/, 'Mobile number must be digits and must start with a country code'),
  cultureCode: Yup.string().required(),
  additionalPassengerDetails: Yup.array().of(
    Yup.object({
      title: Yup.string().required("A title is required"),
      first_name: Yup.string()
        .trim()  
        .required("First name is required"),
      last_name: Yup.string()
        .trim()  
        .required("Last name is required")
    })
  ).optional()
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewFlightRegistrationForm = ({
  userID,
  handleClose
}: {
  userID: string
  handleClose: () => void
}) => {

  const [selectedDate, setSelectedDate] = React.useState<Date | null>(
    new Date
  )

  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }
  const faberToken = OrbitSession.get('faberToken')

  const handleFormSubmit = async (values: NewFlightRegistrationProps) => {
    handleClose()
    const allValues = {
      flight: {
        flight_code: values.flightCode.trim(),
        departure_airport_code: values.departureAirportCode.trim(),
        arrival_airport_code: values.arrivalAirportCode.trim(),
        departure_date: values.departureDate.toISOString().slice(0,10),
      },
      main_passenger: {
        title: values.title,
        first_name: values.firstName.trim(),
        last_name: values.lastName.trim(),
        email: values.email.trim(),
        mobile: values.mobile.trim(),
      },
      additional_passengers: values.additionalPassengerDetails
        .map(passenger => ({
          ...passenger,
          first_name: passenger.first_name.trim(),
          last_name: passenger.last_name.trim()
        })),
      // We'll set the culture code to en-GB (based on formikInitialValues)
      cultureCode: values.cultureCode,
    }

    // Submit details to faber
    try{
      const createFlightRegistrationRes = await CustomerLogAPI.createFlightRegistration(allValues, userID, faberToken)
      if (createFlightRegistrationRes && createFlightRegistrationRes.data.status == "SUCCESS"){
        toast.success('Flight Registration has been created! Auto-refreshing in a few seconds...', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          window.location.reload()
        }, 3000)
      } else {
        toast.error(
          createFlightRegistrationRes.data.error
        )
      }
    } catch (e: unknown) {
      if (e instanceof AxiosError){
        toast.error(
          `${e.response?.data.error}`
        )
      }
    }
  }

  /**
   * Event handlers
   */
  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewFlightRegistrationSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        values
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold variant={"h4"} isUnderlined>Flight Details</NormalText>
            </Grid>
            <ColorBox sx={{ minWidth: '1000px' }}>
              <Grid item>
                <NormalText theme='warning' variant={'subtitle1'}>*Enter details as noted on flight tickets</NormalText>
              </Grid>

              <Grid item xs={3}>
                <NormalText isBold>Flight No</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='flightCode'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'Flight Code'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.flightCode && touched.flightCode)}
                    helperText={errors.flightCode && touched.flightCode ? errors.flightCode : null}
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Departure Airport Code</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='departureAirportCode'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'Departure Airport Code'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.departureAirportCode && touched.departureAirportCode)}
                    helperText={
                      errors.departureAirportCode && touched.departureAirportCode
                        ? errors.departureAirportCode
                        : null
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Arrival Airport Code</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='arrivalAirportCode'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'arrivalAirportCode'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.arrivalAirportCode && touched.arrivalAirportCode)}
                    helperText={
                      errors.arrivalAirportCode && touched.arrivalAirportCode ? errors.arrivalAirportCode : null
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Departure Date (UTC Time)</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <CustomDatePicker
                    label=""
                    date={selectedDate}
                    setDate={setSelectedDate}
                    setFieldVal={setFieldValue}
                    disabled={false}
                  />
                </Box>
              </Grid>
            </ColorBox>
            <Grid item xs={4}>
              <NormalText isBold variant={"h4"} isUnderlined>Primary Passenger Details</NormalText>
            </Grid>
            <ColorBox sx={{ minWidth: '1000px' }}>
              <Grid item xs={3}>
                <NormalText isBold>Title</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Select
                  variant='standard'
                  sx={{ width: '80%' }}
                  onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                  name='title'
                  label='title'
                  defaultValue="Mr"
                >
                  <MenuItem value="Mr">Mr</MenuItem>
                  <MenuItem value="Mrs">Mrs</MenuItem>
                  <MenuItem value="Ms">Ms</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>First Name</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='firstName'
                    type='string'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'firstName'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.firstName && touched.firstName)}
                    helperText={
                      errors.firstName && touched.firstName ? errors.firstName : null
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Last Name</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='lastName'
                    type='string'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'lastName'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.lastName && touched.lastName)}
                    helperText={
                      errors.lastName && touched.lastName ? errors.lastName : null
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Email</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='email'
                    type='string'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'email'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.email && touched.email)}
                    helperText={
                      errors.email && touched.email ? errors.email : null
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <NormalText isBold>Mobile</NormalText>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ width: '80%' }}>
                  <StyledTextField
                    name='mobile'
                    type='string'
                    onChange={(e) => {
                      handleBlur(e)
                      handleChange(e)
                    }}
                    inputProps={{
                      'data-testid': 'mobile'
                    }}
                    fullWidth
                    variant='outlined'
                    error={!!(errors.mobile && touched.mobile)}
                    helperText={
                      errors.mobile && touched.mobile ? errors.mobile : null
                    }
                  />
                </Box>
              </Grid>
            </ColorBox>
            <Grid item xs={4}>
              <NormalText isBold variant={"h4"} isUnderlined>Additional Passenger Details</NormalText>
            </Grid>
            {/* There can be any amount of additional passengers here, so we dynamically increase/decrease here */}
            <FieldArray
              name="additionalPassengerDetails"
              render={arrayHelpers => (
                <Box sx={{ width: '100%' }}>
                  <Grid item xs={12}>
                    <Button onClick={() => arrayHelpers.push({title: 'Mr'})}>Add Passenger</Button>
                  </Grid>
                  {values.additionalPassengerDetails.map((additionalPassengerDetail, index) =>
                  (
                    <ColorBox sx= {{ minWidth: '1000px', mt: 1, key:{index}}}>
                      <Grid>
                        <NormalText isBold>Title</NormalText>
                      </Grid>
                      <Field name={`additionalPassengerDetail.${index}.title`}>
                        {({ field }) => (
                          <Grid item xs={9}>
                            <Select {...field}
                              variant='standard'
                              sx={{ width: '80%' }}
                              name={`additionalPassengerDetails.${index}.title`}
                              label={`additionalPassengerDetails.${index}.title`}
                              defaultValue="Mr"
                            >
                                <MenuItem value="Mr">Mr</MenuItem>
                                <MenuItem value="Mrs">Mrs</MenuItem>
                                <MenuItem value="Ms">Ms</MenuItem>
                            </Select>
                          </Grid>
                        )}
                      </Field>
                      <Grid>
                        <NormalText isBold>First Name</NormalText>
                      </Grid>
                      <Field name={`additionalPassengerDetails.${index}.first_name`}>
                        {({ field, form }) => (
                          <Grid item xs={9}>
                            <Box sx={{ width: '80%' }}>
                              <StyledTextField
                                name={`additionalPassengerDetails.${index}.first_name`}
                                type='string'
                                {...field}
                                onChange={(e) => {
                                  handleBlur(e)
                                  handleChange(e)
                                }}
                                inputProps={{
                                  'data-testid': `additionalPassengerDetails.${index}.first_name`
                                }}
                                fullWidth
                                variant='outlined'
                                error={!!(form.errors.additionalPassengerDetails?.[index]?.first_name && form.touched.additionalPassengerDetails?.[index]?.first_name)}
                                helperText={
                                  form.errors.additionalPassengerDetails?.[index]?.first_name && form.touched.additionalPassengerDetails?.[index]?.first_name ? form.errors.additionalPassengerDetails?.[index]?.first_name : null
                                }
                              />
                            </Box>
                        </Grid>
                        )}
                      </Field>
                      <Grid>
                        <NormalText isBold>Last Name</NormalText>
                      </Grid>
                      <Field name={`additionalPassengerDetails.${index}.last_name`}>
                        {({ field, form }) => (
                          <Grid item xs={9}>
                            <Box sx={{ width: '80%' }}>
                              <StyledTextField
                                name={`additionalPassengerDetails.${index}.last_name`}
                                type='string'
                                {...field}
                                onChange={(e) => {
                                  handleBlur(e)
                                  handleChange(e)
                                }}
                                inputProps={{
                                  'data-testid': `additionalPassengerDetails.${index}.last_name`
                                }}
                                fullWidth
                                variant='outlined'
                                error={!!(form.errors.additionalPassengerDetails?.[index]?.last_name && form.touched.additionalPassengerDetails?.[index]?.last_name)}
                                helperText={
                                  form.errors.additionalPassengerDetails?.[index]?.last_name && form.touched.additionalPassengerDetails?.[index]?.last_name ? form.errors.additionalPassengerDetails?.[index]?.last_name : null
                                }
                              />
                            </Box>
                        </Grid>
                        )}
                      </Field>
                      <br></br>
                      <Grid item xs={4}>
                        <Button onClick={() => arrayHelpers.remove(index)}>Remove Passenger</Button>
                      </Grid>
                    </ColorBox>
                  ))}
                </Box>
              )}
              ></FieldArray>
          </Grid>
          <Grid>
            <Box mt={3}>
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Create Flight Registration</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface AdditionalPassengerDetails {
  title: string,
  first_name: string,
  last_name: string,
}

interface NewFlightRegistrationProps {
  flightCode: string
  departureAirportCode: string
  arrivalAirportCode: string
  departureDate: Date
  title: string
  firstName: string
  lastName: string
  email: string
  mobile: string
  additionalPassengerDetails: AdditionalPassengerDetails[]
  cultureCode: string
}
